import { EmptyState, Skeleton } from '@/components';
import { PageContentCard } from '@/components/layout/PageContentCard';
import { cn } from '@/lib';
import { AlertCircle, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Shared loading / access-denied / error states for the device detail tabs. Every tab
// endpoint has its own permission level, so each tab decides independently which to show.

interface DeviceTabMessageProps {
  title: string;
  description?: string;
}

// DeviceTabAccessDenied shows a neutral no-access placeholder without guessing the cause.
export function DeviceTabAccessDenied({ title, description }: DeviceTabMessageProps) {
  return (
    <PageContentCard className="w-full">
      <EmptyState icon={<Lock />} title={title} description={description} />
    </PageContentCard>
  );
}

// DeviceTabError shows a generic load failure for anything that is not a 403.
export function DeviceTabError({ title, description }: DeviceTabMessageProps) {
  const { t } = useTranslation();
  return (
    <PageContentCard className="w-full">
      <EmptyState icon={<AlertCircle />} title={title} description={description ?? t('errors.generic')} />
    </PageContentCard>
  );
}

interface DeviceTabLoadingProps {
  rows?: number;
  height?: number;
  className?: string;
}

// DeviceTabLoading renders a few placeholder rows while the tab data loads.
export function DeviceTabLoading({ rows = 3, height = 72, className }: DeviceTabLoadingProps) {
  return (
    <div className={cn('flex w-full flex-col gap-3', className)} aria-busy="true">
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton key={index} height={height} rounded="lg" />
      ))}
    </div>
  );
}
